// >>> INSTALL-1's OPENING MEASUREMENT, AND HER RULING ON IT (2026-08-17). <<<
//
// **HER WORDS: *PhonePrompt and InstallPrompt must never stack on one screen. Sequence them.
// Install first. Phone ask on a later clean landing.***
//
// ---------------------------------------------------------------------------------------------
// THE MEASUREMENT (CHK-2), WHICH IS WHAT MAKES THIS A DEFECT RATHER THAN A TIDY-UP.
//
// `PhonePrompt`'s own header says it *"MOUNTS BESIDE `InstallPrompt` AND ASKS THE SAME QUESTIONS OF
// THE SAME PAGE ... because they are the same kind of surface — a once-per-account offer on a clean
// room landing"*, and it names the risk in its own last clause: *"two different notions of MAY WE
// INTERRUPT HER on one screen is how two popups end up stacked."*
//
// **THE TWO NOTIONS AGREED, AND THAT IS EXACTLY WHY THEY STACK.** Both open on a signed-in member
// with a room on a clean landing; App.tsx mounts both unconditionally; neither asks whether the
// other is open. **So a signed-in member with no install and no phone on file meets BOTH FULL-SCREEN
// SHEETS ON ONE SCREEN** — two backdrops, two modals, one of them under the other.
//
// ---------------------------------------------------------------------------------------------
// THE LAW.
//
// **INSTALL OUTRANKS PHONE, AND THE ORDER IS HERS RATHER THAN A COIN FLIP.** It is also the order
// the house's own architecture already implies: the install is the funnel's missing FIRST HOP, and
// on an iPhone the installed app is the ONLY place web push exists — so the phone ask is a second
// channel while the install is the one that unlocks the first.
//
// **AND *LATER* MEANS A LATER LANDING, NEVER LATER ON THIS SCREEN (R18).** The phone ask is not
// queued behind the install sheet to open when it closes: that is the same screen with a delay on
// it, and a member who has just been asked one thing is the worst possible audience for a second
// ask. She is offered the phone on a landing where the install offer is not live at all.
//
// **THE DIRECTION IT FAILS IN IS THE SAFE ONE (R19/R8-4).** Every uncertainty resolves to `install`
// or to `none`, never to `phone`: showing nothing costs a member one ask she will meet next landing,
// and showing both is the defect this exists to end — which renders perfectly and is the thing she
// reported.
//
// Imports NOTHING, so the two components and a `.mjs` gate all read the one declaration.
// ---------------------------------------------------------------------------------------------

/** What may open on this landing. Declared, so a caller cannot invent a fourth and a gate can ask
 *  per key. */
export const LANDING_OFFERS = { INSTALL: "install", PHONE: "phone", NONE: "none" };
export const LANDING_OFFER_VALUES = Object.values(LANDING_OFFERS);

/**
 * THE SEQUENCER. Given whether each once-per-account offer is independently available on this
 * landing, answer which single one may open.
 *
 * Both arguments are asked as `=== true` and never for truthiness (R53/R136/R137): these cross a
 * component boundary and at least one of them is derived from a payload, where `Boolean("false")`
 * is `true` — and a coerced `phone` here is the stacked pair coming straight back.
 */
export function landingOffer({ installAvailable = false, phoneAvailable = false } = {}) {
  if (installAvailable === true) return LANDING_OFFERS.INSTALL;
  if (phoneAvailable === true) return LANDING_OFFERS.PHONE;
  return LANDING_OFFERS.NONE;
}

/**
 * The two ends read the SAME answer rather than each re-deciding (R28/R29) — which is the shape
 * that let them disagree in the first place. A component asks whether IT is the one that may open.
 */
export function mayOpen(offer, which) {
  return landingOffer(offer) === which;
}
